import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { FileUploadModal } from "src/ui/containers/dashboard/files/components/FileUploadModal/FileUploadModal";
import { UploadProvider, useUploadContext } from "src/ui/containers/dashboard/files/context/UploadContext";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";
import { CollapsibleRow, CollapsibleRows, CollapsibleSummary } from "src/ui/shared/table/CollapsibleRow";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { GetFilesParams } from "@dashboard/files/api/rtkClient";
import { FileDropOverlay } from "@dashboard/files/components/FileDropOverlay/FileDropOverlay";
import { UploadProgressBar } from "@dashboard/files/components/UploadProgressBar";
import { useWorkspaceHasFiles } from "@dashboard/files/hooks/useWorkspaceHasFiles";
import { FileInfoWithVersions, normalizeFiles } from "@dashboard/files/utils";
import { FileExplorerHeader } from "@dashboard/shared/components/SidePanel/shared/FileExplorerHeader";
import { FileNameCell } from "@dashboard/shared/components/SidePanel/shared/FileNameCell";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { TagFilter } from "@dashboard/shared/components/TagFilter";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { selectDevicesByGroupId } from "@devices/store/selectors/devices";
import { Group } from "@devices/types";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { EmptyState } from "@shared/EmptyState/EmptyState";
import emptyFilesImage from "@shared/images/EmptyFiles.webp";
import { MinimalTable, MinimalTableCell, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader, TableHeaderRows } from "@shared/table/TableHeader";
import { TableRowCells } from "@shared/table/TableRowCells";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import ActionsCell from "./components/ActionsCell";
import { columns, SourceCell, useParams } from "./FileTab.utils";
import { GroupDevicesProvider } from "./GroupDevicesContext";

export type GroupFileTabParams = Required<
    Pick<GetFilesParams, "workspaceId" | "sortCriteria" | "sortDirection" | "groups" | "visibilities" | "includeParentFiles">
>;

const FileTabContent = (props: Group) => {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [params, setParams] = useParams(props.id);

    const { t } = useTranslation(["sidepanels", "common", "modals", "pages"]);
    const { uploadStarted } = useUploadContext();

    const {
        data: files,
        isFetching,
        isLoading,
        isError,
        refetch,
    } = useAllCurrentWorkspaceFiles(params, { preferCacheValue: false });
    const data = React.useMemo(() => normalizeFiles(files), [files]);
    const tableLoading = useTableFetchLoading(isFetching, isLoading, params);
    const workspaceHasFiles = useWorkspaceHasFiles();
    const hasNoFiles = workspaceHasFiles === false || (!isLoading && !isFetching && !isError && data.length === 0);

    const { data: workspace } = useCurrentWorkspace();

    // Only the devices whose license sits directly in this group, which is exactly what a group
    // command reaches — subgroups are not included on that path.
    const devices = useSelector((state) => selectDevicesByGroupId(state, props.id));

    const { groupAbility } = PermissionService();
    const mayManageFile = groupAbility(props, workspace).can("manage", "File");

    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");
    const isExpired = useCurrentSubscriptionExpired();
    const uploadDisabled = !mayManageFile || isOverlimit || isExpired || uploadStarted;

    const onIncludeParentFilesChange = (value: boolean) => {
        setParams((prev) => ({ ...prev, includeParentFiles: value }));
    };

    const table = useReactTable<FileInfoWithVersions>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta: { selectedGroupId: props.id },
    });

    const getAddBtnTitle = () => {
        const result = { title: t("sidepanels:groupPanel.tooltip.uploadFile") };

        if (uploadStarted) {
            result.title = t("pages:filesPage.tooltips.uploadInProgress");
        }
        if (isExpired) {
            result.title = t("sidepanels:groupPanel.tooltip.subscriptionExpired");
        }
        if (!mayManageFile) {
            result.title = t("sidepanels:groupPanel.tooltip.uploadFileError");
        }
        if (isOverlimit) {
            result.title = t("sidepanels:groupPanel.tooltip.uploadFileLimit");
        }
        return result;
    };

    const onSort = (id: string) => {
        setParams((prev) => ({
            ...prev,
            sortCriteria: id,
            sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
        }));
    };

    return (
        <GroupDevicesProvider devices={devices}>
            <PaneElement.Container>
                <PaneElement.TitleRow title={t("sidepanels:groupPanel.filesTab.availableFiles")}>
                    {hasNoFiles ? null : (
                        <SecondaryButton
                            onClick={() => setIsDialogOpen(true)}
                            tooltipProps={getAddBtnTitle()}
                            disabled={uploadDisabled}
                        >
                            <i className="fa-solid fa-upload" />
                        </SecondaryButton>
                    )}
                </PaneElement.TitleRow>
                {workspaceHasFiles === false ? null : (
                    <FileExplorerHeader
                        selectedGroupId={props.id}
                        includeParentFiles={params.includeParentFiles}
                        onIncludeParentFilesChange={onIncludeParentFilesChange}
                    />
                )}

                {uploadStarted ? <UploadProgressBar /> : null}
                {hasNoFiles ? (
                    <EmptyState
                        size="compact"
                        image={emptyFilesImage}
                        title={t("pages:filesPage.emptyState.title")}
                        description={t("pages:filesPage.emptyState.description")}
                        action={
                            <PrimaryButton
                                onClick={() => setIsDialogOpen(true)}
                                disabled={uploadDisabled}
                                startIcon={<i className="fa-solid fa-upload" />}
                                tooltipProps={{ ...getAddBtnTitle(), hide: !uploadDisabled }}
                                name="upload-file"
                            >
                                {t("common:button.upload")}
                            </PrimaryButton>
                        }
                    />
                ) : (
                    <PaneElement.TableLayout
                        contentDeps={[table.getRowModel().rows.length]}
                        renderWrapper={(wrapperProps) => (
                            <FileDropOverlay
                                {...wrapperProps}
                                groupId={props.id}
                                disabled={uploadDisabled}
                                disabledReason={getAddBtnTitle().title}
                            />
                        )}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                <TableHeaderRows
                                    headerGroups={table.getHeaderGroups()}
                                    renderHeader={(header) => (
                                        <CustomTableHeader
                                            sortCriteria={params.sortCriteria}
                                            sortDirection={params.sortDirection}
                                            onSort={onSort}
                                            {...header}
                                        >
                                            {/* TODO: fill out TagFilter after the tag filtering is implemented on the backend. */}
                                            {header.id === "filesTags" ? (
                                                <TagFilter tags={[]} filters={[]} onChange={() => {}} />
                                            ) : null}
                                        </CustomTableHeader>
                                    )}
                                />
                            </MinimalTableHeader>

                            <TableBody<FileInfoWithVersions>
                                table={table}
                                noDataMessage={t("sidepanels:groupPanel.table.noFiles")}
                                loading={tableLoading}
                                error={{ isError, onError: refetch }}
                            >
                                {({ rows }) =>
                                    rows.map((row) => (
                                        <CollapsibleRow key={row.id}>
                                            <CollapsibleSummary disabled={row.original.olderVersions.length === 0} row={row}>
                                                <TableRowCells row={row} />
                                            </CollapsibleSummary>
                                            <CollapsibleRows>
                                                {row.original.olderVersions.map((rowItem) => (
                                                    <MinimalTableRow key={rowItem.id}>
                                                        <MinimalTableCell />
                                                        <FileNameCell file={rowItem} />
                                                        <SourceCell file={rowItem} selectedGroupId={props.id} />
                                                        <MinimalTableCell style={{ overflow: "visible" }}>
                                                            <ActionsCell file={rowItem} isOlderVersion />
                                                        </MinimalTableCell>
                                                    </MinimalTableRow>
                                                ))}
                                            </CollapsibleRows>
                                        </CollapsibleRow>
                                    ))
                                }
                            </TableBody>
                        </MinimalTable>
                    </PaneElement.TableLayout>
                )}

                <FileUploadModal
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    onUploadStarted={() => setIsDialogOpen(false)}
                    groupId={props.id}
                    groupName={props.name}
                    disabled={uploadDisabled}
                    hint={t("modals:uploadFile.dragHint")}
                />
            </PaneElement.Container>
        </GroupDevicesProvider>
    );
};

const FileTab = (props: Group) => (
    <UploadProvider>
        <FileTabContent {...props} />
    </UploadProvider>
);

export default FileTab;
